/**
 * AreaHistoryDialog — admin view of a named area:
 *  - Edit name / position / size
 *  - Delete the area
 *  - View read-only history of internal findings (technician-only data, never shown to client)
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { History, ShieldAlert, Save, Trash2 } from "lucide-react";

const RISK_LBL = { bajo: "Bajo (OK)", medio: "Medio (Atención)", alto: "Alto (Alerta)" };
const PEST_LBL = {
    raton: "Ratón",
    rata: "Rata",
    insectos_rastreros: "Insectos rastreros",
    insectos_voladores: "Insectos voladores",
};

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("es-SV", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso?.slice(0, 16) || ""; }
};

export default function AreaHistoryDialog({ open, area, planId, findings = [], onClose, onUpdated }) {
    const [form, setForm] = useState({ name: "", x: 0, y: 0, w: 0, h: 0 });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (area) {
            setForm({
                name: area.name || "",
                x: area.x ?? 0,
                y: area.y ?? 0,
                w: area.w ?? 10,
                h: area.h ?? 10,
            });
        }
    }, [area]);

    if (!area) return null;
    const list = findings
        .filter((f) => f.area_id === area.area_id)
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

    const save = async () => {
        if (!planId) return;
        if (!form.name.trim()) {
            toast.error("El nombre del área no puede estar vacío");
            return;
        }
        setBusy(true);
        try {
            await api.put(`/floor-plans/${planId}/areas/${area.area_id}`, {
                name: form.name.trim(),
                x: Number(form.x),
                y: Number(form.y),
                w: Number(form.w),
                h: Number(form.h),
            });
            toast.success("Área actualizada");
            onUpdated?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo actualizar");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!planId) return;
        if (!window.confirm(`¿Eliminar el área "${area.name}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/floor-plans/${planId}/areas/${area.area_id}`);
            toast.success("Área eliminada");
            onUpdated?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo eliminar");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="area-history-dialog">
                <DialogHeader>
                    <DialogTitle>Área · {area.name}</DialogTitle>
                </DialogHeader>

                {/* Edit form */}
                <div className="space-y-3" data-testid="area-edit-form">
                    <div>
                        <Label className="text-xs">Nombre del área</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="area-edit-name"
                        />
                    </div>
                    <p className="text-[11px] text-slate-500">
                        Para mover o redimensionar el área, arrástrala o usa los puntos de control directamente en el plano.
                    </p>
                </div>

                {/* History */}
                <div className="border-t pt-3 mt-2">
                    <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 flex gap-2 items-start mb-2">
                        <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <span className="text-amber-800">
                            Los hallazgos son <b>internos</b> (técnicos/admin). No se muestran al cliente.
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 mb-2">
                        <History className="w-3.5 h-3.5 text-slate-500" />
                        <span className="text-xs font-semibold text-slate-700">Historial ({list.length})</span>
                    </div>
                    {list.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-2">Sin hallazgos registrados.</div>
                    ) : (
                        <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="area-history-list">
                            {list.slice(0, 10).map((h) => (
                                <li key={h.id} className="text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1.5"
                                    data-testid={`admin-area-history-${h.id}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-slate-500">{fmtDate(h.created_at)}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-white text-[9px] font-semibold ${
                                            h.risk_level === "alto" ? "bg-red-600" :
                                            h.risk_level === "medio" ? "bg-orange-500" : "bg-emerald-600"
                                        }`}>
                                            {RISK_LBL[h.risk_level] || h.risk_level}
                                        </span>
                                    </div>
                                    <div className="text-slate-700 mt-0.5">
                                        {PEST_LBL[h.pest_type] || h.pest_type}{h.notes ? ` — ${h.notes}` : ""}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <DialogFooter className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={remove}
                        disabled={busy}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="area-delete"
                    >
                        <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </Button>
                    <div className="flex-1" />
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="area-history-close">
                        Cancelar
                    </Button>
                    <Button onClick={save} disabled={busy} data-testid="area-save">
                        <Save className="w-4 h-4 mr-1" /> Guardar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
